import React from 'react';
import { Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import { formatPaymentStatusLabel, getPaymentStatusColor } from '../../Helpers/paymentStatusHelpers';

interface PaymentSummaryProps {
  totalPrice: number;
  totalPaid: number;
  paymentsCount: number;
  refundsCount: number;
}

const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  totalPrice,
  totalPaid,
  paymentsCount,
  refundsCount,
}) => {
  const remainingBalance = totalPrice - totalPaid;
  const progressPercentage = totalPrice > 0 ? Math.min((totalPaid / totalPrice) * 100, 100) : 0;

  const getStatusConfig = () => {
    if (totalPaid > totalPrice) {
      return {
        label: formatPaymentStatusLabel('Nadpłata', totalPrice, totalPaid),
        color: getPaymentStatusColor('Nadpłata'),
      };
    }
    if (totalPaid >= totalPrice) {
      return {
        label: formatPaymentStatusLabel('Opłacona', totalPrice, totalPaid),
        color: getPaymentStatusColor('Opłacona'),
      };
    }
    if (totalPaid > 0) {
      return {
        label: formatPaymentStatusLabel('Częściowo opłacona', totalPrice, totalPaid),
        color: getPaymentStatusColor('Częściowo opłacona'),
      };
    }
    return {
      label: formatPaymentStatusLabel('Nieopłacona', totalPrice, totalPaid),
      color: getPaymentStatusColor('Nieopłacona'),
    };
  };

  const status = getStatusConfig();

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
      }}
    >
      {/* Status Badge */}
      <Box sx={{ mb: 2 }}>
        <Chip label={status.label} color={status.color} sx={{ fontWeight: 'bold' }} />
      </Box>

      {/* Progress Bar */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Postęp płatności
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          sx={{ height: 10, borderRadius: 1, mb: 1 }}
        />
        <Typography variant="caption">
          {totalPaid.toFixed(2)} PLN / {totalPrice.toFixed(2)} PLN
        </Typography>
      </Box>

      {/* Remaining Balance */}
      {remainingBalance > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Do zapłaty: <strong>{remainingBalance.toFixed(2)} PLN</strong>
        </Typography>
      )}

      {/* Payment Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="body2">
          Płatności: <strong>{paymentsCount}</strong>
        </Typography>
        {refundsCount > 0 && (
          <Typography variant="body2">
            Zwroty: <strong>{refundsCount}</strong>
          </Typography>
        )}
      </Stack>

    </Box>
  );
};

export default PaymentSummary;
